import _ from "lodash";
import React from "react";
import { useEffect, useState } from "react";
import {
  Dropdown,
  Header,
  Icon,
  Input,
  Label,
  Table,
  TableHeaderCell,
} from "semantic-ui-react";

import { DATA } from "./api";

import "./Config.css";
import { featuresStorage } from "./features";
import { LocalStorage } from "./LocalStorage";

export const preferencesStorage = new LocalStorage("player-options");

export const LOOP_PLAYLIST = "loop-playlist";
export const LOOP_TRACK = "loop-track";
export const NO_LOOP = "no-loop";
export const EDITOR = "editor";

const loopModes = [
  { key: LOOP_PLAYLIST, text: "Playlist", value: LOOP_PLAYLIST },
  { key: LOOP_TRACK, text: "Track", value: LOOP_TRACK },
  { key: NO_LOOP, text: "No", value: NO_LOOP },
];

const cardFormats = [
  { key: "normal", text: "Normal", value: "normal" },
  { key: "small", text: "Small", value: "small" },
];

const featuresDefault = [
  {
    key: "includeRating",
    label: "Tracks rating",
    enabled: false,
  },
  {
    key: "saveEnabled",
    label: "Save playlist",
    enabled: false,
  },
  {
    key: "saveToClipboard",
    label: "Save to clipboard",
    enabled: false,
  },
  {
    key: "includeTour",
    label: "Player tour",
    enabled: false,
  },
  {
    key: "tourStartsOnLoad",
    label: "Tour auto-start",
    enabled: false,
  },
  {
    key: "includeDescription",
    label: "Track descriptions",
    enabled: false,
  },
].sort((a, b) => (a.label > b.label ? 1 : 0));

const preferencesDefault = [
  {
    key: "loopMode",
    label: "Loop mode",
    value: NO_LOOP,
    options: loopModes,
    nullable: false,
  },
  {
    key: "cardFormat",
    label: "Track cards",
    value: "normal",
    options: cardFormats,
    nullable: false,
  },
  {
    key: "lyricsActive",
    label: "Lyrics subtitle",
    value: false,
  },
  {
    key: "defaultPlaylist",
    label: "Default playlist",
    value: null,
    options: [],
    nullable: true,
  },
  {
    key: "snoozeDuration",
    label: "Snooze duration",
    value: 300,
    nullable: true,
    asNumber: true,
  },
  {
    key: "darkMode",
    label: "Dark mode",
    value: false,
  },
].sort((a, b) => (a.label > b.label ? 1 : 0));

const currentPreferences = () => {
  const current = preferencesStorage.restore();

  _.forEach(current, (value, key) => {
    const option = preferencesDefault.find((e) => e.key === key);

    if (option) option.value = value;
  });

  return preferencesDefault;
};

const currentFeatures = () => {
  const current = featuresStorage.restore();

  _.forEach(current, (value, key) => {
    const feature = featuresDefault.find((e) => e.key === key);

    if (feature) feature.enabled = value;
  });

  return featuresDefault;
};

function Checkbox({ checked, onChange }) {
  return checked ? (
    <Icon
      color="green"
      name="checkmark"
      size="large"
      onClick={() => onChange(false)}
    />
  ) : (
    <Icon color="red" name="x" size="large" onClick={() => onChange(true)} />
  );
}

export function Config() {
  const [features, setFeatures] = useState(currentFeatures());
  const [preferences, setPreferences] = useState([]);

  useEffect(() => {
    loadAlbums().then((options) => {
      const option = currentPreferences().find(
        (e) => e.key === "defaultPlaylist"
      );

      option.options = options;

      setPreferences(preferencesDefault);
    });
  }, [preferences, setPreferences]);

  return (
    <div className="config-app">
      <Header as="h1">Player configuration</Header>

      <div className="config-tables">
        <Table definition>
          <Table.Header>
            <Table.Row>
              <TableHeaderCell>Feature</TableHeaderCell>
              <TableHeaderCell>Enable</TableHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {features.map((feature) => {
              return (
                <Table.Row>
                  <Table.Cell>{feature.label}</Table.Cell>
                  <Table.Cell>
                    <Checkbox
                      checked={feature.enabled}
                      onChange={(checked) => {
                        feature.enabled = checked;
                        featuresStorage.save(feature.key, checked);

                        setFeatures([...features]);
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        <Table definition>
          <Table.Header>
            <Table.Row>
              <TableHeaderCell>Option</TableHeaderCell>
              <TableHeaderCell>Value</TableHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {preferences.map((option) => {
              return (
                <Table.Row>
                  <Table.Cell>{option.label}</Table.Cell>
                  <Table.Cell>
                    {option.options ? (
                      <Dropdown
                        search
                        selection
                        clearable={option.nullable}
                        options={option.options}
                        value={option.value}
                        onChange={(_e, { value }) => {
                          option.value = value;
                          preferencesStorage.save(option.key, value);

                          setPreferences([...preferences]);
                        }}
                      />
                    ) : option.asNumber ? (
                      <Input
                        type="number"
                        value={option.value}
                        onChange={(_ev, x) => {
                          option.value = parseInt(x.value);
                          preferencesStorage.save(option.key, option.value);

                          setPreferences([...preferences]);
                        }}
                      />
                    ) : (
                      <Checkbox
                        checked={option.value}
                        onChange={(checked) => {
                          option.value = checked;
                          preferencesStorage.save(option.key, checked);

                          setPreferences([...preferences]);
                        }}
                      />
                    )}
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>

        <Label color="purple">Ver. {global.version}</Label>
      </div>
    </div>
  );
}

function loadAlbums() {
  return fetch(`${DATA}/albums.json`)
    .then((response) => {
      return response.json();
    })
    .then((list) => {
      return list.map((album) => {
        return {
          key: album.name,
          value: album.name,
          text: album.name
            .replace(/[-]/g, " ")
            .replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())),
        };
      });
    });
}
